import { useState } from 'react';
import { Modal, Button, Input } from '../common';
import { useUIStore, useMemeStore, useNFTStore } from '../../stores';
import { fetchNFTByNumber } from '../../services/solana';
import type { NFTData } from '../../types';

export function MemeNFTSearch() {
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewNFT, setPreviewNFT] = useState<NFTData | null>(null);

  const { activeModal, closeModal } = useUIStore();
  const { addNFTElement } = useMemeStore();
  const { fetchedNFTs } = useNFTStore();

  const isOpen = activeModal === 'memeSearch';

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const nftNumber = parseInt(searchValue);
    if (isNaN(nftNumber) || nftNumber <= 0) {
      setError('Please enter a valid NFT number');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPreviewNFT(null);

    const cached = fetchedNFTs.get(nftNumber);
    if (cached) {
      setPreviewNFT(cached);
      setIsLoading(false);
      return;
    }

    const result = await fetchNFTByNumber(nftNumber);

    if (result.success && result.data) {
      setPreviewNFT(result.data);
    } else {
      setError(result.error || 'Failed to fetch NFT');
    }

    setIsLoading(false);
  };

  const handleAddToMeme = () => {
    if (previewNFT) {
      addNFTElement(previewNFT);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchValue('');
    setError(null);
    setPreviewNFT(null);
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add NFT to Meme"
      size="md"
      footer={
        previewNFT ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddToMeme}>
              Add to Meme
            </Button>
          </>
        ) : null
      }
    >
      <form onSubmit={handleSearch} className="space-y-4">
        <Input
          label="NFT Number"
          type="number"
          placeholder="Enter NFT # (e.g., 42)"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          error={error}
          min={1}
        />

        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full"
        >
          Search NFT
        </Button>
      </form>

      {previewNFT && (
        <div className="mt-6 p-4 bg-surface-secondary dark:bg-neutral-800 rounded-lg">
          <div className="flex gap-4">
            <img
              src={previewNFT.imageUrl}
              alt={previewNFT.name}
              className="w-24 h-24 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h4 className="font-semibold text-text-dark dark:text-neutral-100">{previewNFT.name}</h4>
              <p className="text-sm text-text-muted dark:text-neutral-400 mt-1">
                {previewNFT.collection?.name || 'Unknown Collection'}
              </p>
              {previewNFT.layerAssets.length > 0 && (
                <p className="text-xs text-game-blue mt-2">
                  {previewNFT.layerAssets.length} layer(s) available for editing
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
